/**
 * A one-line text input. Put it inside a `Field` for its label, hint and error.
 *
 * `search` adds a clear button once there is text. `number` aligns right in tabular figures, so a
 * column of doses lines up. `prefix` and `suffix` hold a short unit or symbol ("$", "mg") inside
 * the box, read out with the value through `aria-describedby`.
 *
 * Set `type`, `autoComplete` and `inputMode` on every input that has them. They decide which
 * keyboard a phone shows and what the browser can fill in.
 */
import * as stylex from "@stylexjs/stylex";
import type { KeyboardEvent } from "react";
import { useId, useRef } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { controlStyle } from "./controlStyle.ts";
import { focusRing } from "./focus.ts";
import { IconButton } from "./IconButton.tsx";
import { CloseIcon } from "./icons.tsx";
const _temp = {
  kZCmMZ: "xnvo3vl",
  "$$css": true
};
const _temp2 = {
  kwRFfy: "x120eax6",
  "$$css": true
};
const styles = {
  number: {
    k9WMMc: "xp4054r",
    kcqcaj: "x1l1vl4p",
    $$css: true
  },
  padStart: (chars: number) => [_temp, {
    "--x-paddingInlineStart": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(`calc(${String(chars)}ch + ${space.md} * 2)`)
  }],
  padEnd: (chars: number) => [_temp2, {
    "--x-paddingInlineEnd": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(`calc(${String(chars)}ch + ${space.md} * 2)`)
  }],
  dateRing: {
    k3XXqK: "x1t137rt x9v5kkp x14xc8xn",
    $$css: true
  },
  padClear: {
    kwRFfy: "xsghba",
    $$css: true
  }
};
export type InputType = "text" | "email" | "search" | "number" | "password" | "tel" | "url" | "date";
export interface InputProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly id?: string | undefined;
  readonly type?: InputType;
  readonly size?: "md" | "lg";
  readonly name?: string;
  readonly placeholder?: string | undefined;
  readonly autoComplete?: string | undefined;
  readonly inputMode?: "text" | "email" | "numeric" | "decimal" | "tel" | "url" | "search";
  readonly enterKeyHint?: "done" | "go" | "next" | "search" | "send";
  readonly maxLength?: number | undefined;
  readonly min?: number | string;
  readonly max?: number | string;
  readonly step?: number | "any";
  readonly pattern?: string;
  readonly required?: boolean | undefined;
  readonly "aria-required"?: true | undefined;
  readonly disabled?: boolean;
  readonly readOnly?: boolean;
  readonly autoFocus?: boolean | undefined;
  readonly prefix?: string;
  readonly suffix?: string;
  /** For search: what the clear button says, "Clear search" unless the search is of something. */
  readonly clearLabel?: string;
  readonly onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  readonly onBlur?: () => void;
  readonly "aria-describedby"?: string | undefined;
  readonly "aria-invalid"?: true | undefined;
  readonly "aria-label"?: string;
  readonly testId?: string;
}
export function Input({
  value,
  onChange,
  type = "text",
  size = "md",
  prefix,
  suffix,
  clearLabel = "Clear search",
  disabled = false,
  readOnly = false,
  testId,
  "aria-describedby": describedBy,
  "aria-invalid": invalid,
  ...rest
}: InputProps) {
  const affixId = useId();
  const field = useRef<HTMLInputElement>(null);
  const clearable = type === "search" && value !== "" && !disabled && !readOnly;
  const affixed = prefix !== undefined || suffix !== undefined;
  const described = [describedBy, affixed ? affixId : undefined].filter(Boolean).join(" ");
  return <span className="x1n2onr6 x78zum5 xh8yej3 xeuugli">
      <input ref={field} {...rest}
    /* A search field draws its own clear button, so it skips the browser's second one. */ type={type === "search" ? "text" : type} role={type === "search" ? "searchbox" : undefined} value={value} disabled={disabled} readOnly={readOnly} aria-invalid={invalid} aria-describedby={described === "" ? undefined : described} onChange={event => onChange(event.currentTarget.value)} data-testid={testId} {...stylex.props(controlStyle.box, controlStyle[size], type === "number" && styles.number, prefix !== undefined && styles.padStart(prefix.length), suffix !== undefined && styles.padEnd(suffix.length), clearable && styles.padClear, invalid === true && controlStyle.invalid, readOnly && controlStyle.readOnly, disabled && controlStyle.disabled, focusRing, type === "date" && styles.dateRing)} />
      {prefix !== undefined && <span aria-hidden="true" className="x10l6tqk x10no89f x78zum5 x6s0dn4 x1lm7cez x47corl x1l1vl4p x9mlwl9">
          {prefix}
        </span>}
      {suffix !== undefined && <span aria-hidden="true" className="x10l6tqk x10no89f x78zum5 x6s0dn4 x1lm7cez x47corl x1l1vl4p xmzullg">
          {suffix}
        </span>}
      {affixed && <span id={affixId} hidden>
          {[prefix, suffix].filter(Boolean).join(" ")}
        </span>}
      {clearable && <span className="x10l6tqk x10no89f x1o4coni x78zum5 x6s0dn4">
          <IconButton label={clearLabel} icon={<CloseIcon />} size="sm" tipAlign="end" onClick={() => {
        onChange("");
        field.current?.focus();
      }} />
        </span>}
    </span>;
}